import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import './Accounts.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  'https://api-ebz57wajla-uc.a.run.app';

const Accounts = () => {
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellingSubscription, setCancellingSubscription] = useState(null);
  const [payfastApiStatus, setPayfastApiStatus] = useState(null);
  const [testingPayfastApi, setTestingPayfastApi] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      if (authUser) {
        setUser(authUser);
        fetchSubscriptions(authUser);
      } else {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchSubscriptions = async (authUser) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching current tier for:', authUser.email);
      
      const response = await fetch(`${API_BASE_URL}/user/current-tier`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          userId: authUser.uid,
          email: authUser.email 
        })
      });

      console.log('Response status:', response.status);
      console.log('Response content-type:', response.headers.get('content-type'));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Check if response is JSON or HTML
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error('Expected JSON but got:', contentType, responseText.substring(0, 500));
        throw new Error(`API returned ${contentType || 'unexpected content'} instead of JSON`);
      }

      const data = await response.json();
      console.log('API Response:', data);
      
      if (data.success) {
        // Convert single tier info to subscription format for UI compatibility
        const currentTier = data.currentTier || 'FREE';
        const subscription = data.subscription;
        
        const singleSubscription = [{
          id: subscription?.token || `TIER_${currentTier}_${authUser.uid}`,
          plan: currentTier,
          status: 'active',
          amount: subscription?.amount || 0,
          email: authUser.email,
          created_at: subscription?.created_at || new Date().toISOString(),
          last_payment: subscription?.last_payment,
          next_billing_date: subscription?.next_billing_date,
          isCurrent: true // Mark as current/active tier
        }];
        
        setSubscriptions(singleSubscription);
        console.log('Set current tier subscription:', singleSubscription);
      } else {
        setError(data.message || 'Failed to fetch current tier');
      }
    } catch (err) {
      console.error('Error fetching current tier:', err);
      setError(`Unable to load account information: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSubscriptions = async (authUser) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching comprehensive subscription data with PayFast API integration for admin:', authUser.email);
      
      // Fetch comprehensive PayFast + Firebase data
      const response = await fetch(`${API_BASE_URL}/payfast/subscriptions/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PayFast API Error:', errorText);
        throw new Error(`Failed to fetch comprehensive subscription data: ${response.status}`);
      }

      const data = await response.json();
      console.log('PayFast API Response:', data);

      if (data.success) {
        // Process PayFast data
        const payfastSubscriptions = data.payfast?.subscriptions || [];
        const firebaseSubscriptions = data.firebase?.subscriptions || [];
        
        console.log(`Found ${payfastSubscriptions.length} PayFast subscriptions and ${firebaseSubscriptions.length} Firebase subscriptions`);
        
        // Combine and format subscription data
        const allSubscriptions = [
          ...firebaseSubscriptions.map(sub => ({
            ...sub,
            dataSource: 'Firebase',
            displayName: sub.email || 'Unknown',
            planName: sub.plan || 'Unknown',
            amount: sub.amount || 0,
            status: sub.status || 'unknown',
            lastUpdated: sub.lastUpdated || 'Unknown'
          })),
          ...payfastSubscriptions.map((sub, index) => ({
            id: sub.token || sub.id || `payfast_${index}`,
            email: sub.email || 'Unknown',
            plan: sub.plan_name || 'Unknown',
            amount: parseFloat(sub.amount || 0),
            status: sub.status || 'unknown',
            lastUpdated: sub.created_date || 'Unknown',
            dataSource: 'PayFast API',
            displayName: sub.email || 'Unknown',
            planName: sub.plan_name || 'Unknown',
            nextBilling: sub.next_billing_date || 'Unknown'
          }))
        ];
        
        setAllSubscriptions(allSubscriptions);
        setIsAdmin(true);
        console.log('Set comprehensive subscription data:', allSubscriptions);
      } else {
        console.log('PayFast API failed, falling back to Firebase admin API...');
        throw new Error(data.error || 'PayFast API failed');
      }
    } catch (err) {
      console.error('Error fetching comprehensive subscriptions:', err);
      // If admin fetch fails, user might not be admin - continue with normal flow
      setIsAdmin(false);
      setShowAllUsers(false);
      setError(`Admin access failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminView = () => {
    if (!isAdmin) {
      fetchAllSubscriptions(user);
    } else {
      setShowAllUsers(!showAllUsers);
    }
  };

  const cancelSubscription = async (subscriptionId, planName) => {
    // Don't allow cancelling FREE tier
    if (planName === 'FREE') {
      alert('You cannot cancel the FREE tier. It\'s already free!');
      return;
    }
    
    const confirmCancel = window.confirm(
      `Are you sure you want to downgrade from ${planName} to FREE tier? This will cancel your paid subscription.`
    );

    if (!confirmCancel) return;

    try {
      setCancellingSubscription(subscriptionId);
      
      const response = await fetch(`${API_BASE_URL}/user/downgrade-to-free`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      const result = await response.json();
      
      if (result.success) {
        alert('Successfully downgraded to FREE tier!');
        // Refresh current tier info
        fetchSubscriptions(user);
      } else {
        alert(result.message || 'Failed to cancel subscription. Please try again.');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      alert('Unable to cancel subscription. Please try again later.');
    } finally {
      setCancellingSubscription(null);
    }
  };

  const refreshSubscriptions = () => {
    if (user) {
      fetchSubscriptions(user);
    }
  };

  const testPayfastApi = async () => {
    try {
      setTestingPayfastApi(true);
      
      const response = await fetch(`${API_BASE_URL}/admin/payfast/test`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': user?.accessToken || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      const result = await response.json();
      setPayfastApiStatus(result.payfast);
      
      if (result.success && result.payfast.connected) {
        alert(`🎉 PayFast API Connected Successfully!\n\nMode: ${result.payfast.mode}\nMerchant: ${result.payfast.merchantId}\nTimestamp: ${new Date(result.payfast.timestamp).toLocaleString()}`);
      } else {
        alert(`❌ PayFast API Connection Failed\n\nError: ${result.payfast.error || 'Unknown error'}\nMode: ${result.payfast.mode}\nCheck your PayFast credentials.`);
      }
    } catch (error) {
      console.error('PayFast API test failed:', error);
      alert(`PayFast API Test Failed: ${error.message}`);
      setPayfastApiStatus({ connected: false, error: error.message });
    } finally {
      setTestingPayfastApi(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return '#48bb78';
      case 'cancelled':
      case 'canceled':
        return '#e53e3e';
      case 'pending':
        return '#f6ad55';
      case 'suspended':
        return '#ed8936';
      default:
        return '#718096';
    }
  };

  if (loading) {
    return (
      <div className="accounts-container">
        <div className="accounts-header">
          <h1>Your Account</h1>
          <button onClick={() => navigate('/Dashboard')} className="back-button">
            ← Back to Dashboard
          </button>
        </div>
        <div className="loading-message">
          <p>Loading your account information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="accounts-container">
      <div className="accounts-header">
        <h1>Your Account</h1>
        <div className="header-actions">
          <button onClick={toggleAdminView} className="admin-toggle-button" style={{
            backgroundColor: showAllUsers ? '#e53e3e' : '#4299e1',
            color: 'white',
            margin: '0 5px'
          }}>
            {showAllUsers ? '👤 Your Account' : '👥 All Users'}
          </button>
          {isAdmin && (
            <button 
              onClick={testPayfastApi} 
              disabled={testingPayfastApi}
              className="api-test-button" 
              style={{
                backgroundColor: payfastApiStatus?.connected ? '#48bb78' : '#f56565',
                color: 'white',
                margin: '0 5px'
              }}
            >
              {testingPayfastApi ? '🔄 Testing...' : '🔗 Test PayFast API'}
            </button>
          )}
          <button onClick={refreshSubscriptions} className="refresh-button">
            🔄 Refresh
          </button>
          <button onClick={() => navigate('/Dashboard')} className="back-button">
            ← Back to Dashboard
          </button>
        </div>
      </div>

      <div className="accounts-content">
        {error && (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={refreshSubscriptions} className="retry-button">
              Try Again
            </button>
          </div>
        )}

        <div className="subscriptions-info">
          <p>
            <strong>Email:</strong> {user?.email}
          </p>
          {!showAllUsers && (
            <p>
              <strong>Current Tier:</strong> {subscriptions.length > 0 ? subscriptions[0].plan : 'Loading...'}
            </p>
          )}
          {showAllUsers && allSubscriptions.length > 0 && (
            <div className="admin-summary">
              <p><strong>👥 Total Users:</strong> {allSubscriptions.length}</p>
              <p><strong>💰 Total Revenue:</strong> R{allSubscriptions.reduce((sum, s) => sum + (s.amount || 0), 0).toFixed(2)}/month</p>
              <p><strong>📊 Breakdown:</strong> 
                TIER_2: {allSubscriptions.filter(s => s.plan === 'TIER_2').length}, 
                TIER_1: {allSubscriptions.filter(s => s.plan === 'TIER_1').length}, 
                FREE: {allSubscriptions.filter(s => s.plan === 'FREE').length}
              </p>
            </div>
          )}
        </div>

        {showAllUsers ? (
          // Admin view - show all user subscriptions with PayFast integration
          allSubscriptions.length === 0 ? (
            <div className="no-subscriptions">
              <h3>No Subscription Data Available</h3>
              <p>Unable to load subscription information.</p>
            </div>
          ) : (
            <div className="subscriptions-grid">
              {allSubscriptions.map((subscription, index) => (
                <div key={subscription.userId || index} className="subscription-card">
                  <div className="subscription-header">
                    <h3>{subscription.email || 'Unknown'}</h3>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(subscription.status) }}
                    >
                      {subscription.plan}
                    </span>
                  </div>

                  <div className="subscription-details">
                    <div className="detail-row">
                      <span className="label">User ID:</span>
                      <span className="value" style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                        {subscription.userId.length > 25 ? subscription.userId.substring(0, 25) + '...' : subscription.userId}
                      </span>
                    </div>
                    
                    <div className="detail-row">
                      <span className="label">Plan:</span>
                      <span className="value">{subscription.plan}</span>
                    </div>
                    
                    <div className="detail-row">
                      <span className="label">Monthly Cost:</span>
                      <span className="value">
                        {subscription.amount === 0 ? 'FREE' : `R${subscription.amount.toFixed(2)}/month`}
                      </span>
                    </div>

                    <div className="detail-row">
                      <span className="label">Monthly Limit:</span>
                      <span className="value">{subscription.monthlyLimit} submissions</span>
                    </div>

                    {/* PayFast Integration Status */}
                    <div className="detail-row">
                      <span className="label">PayFast Status:</span>
                      <span className="value" style={{ 
                        color: subscription.payfastConnected ? '#48bb78' : '#e53e3e',
                        fontWeight: 'bold'
                      }}>
                        {subscription.payfastConnected ? '🟢 Connected' : '🔴 Disconnected'}
                      </span>
                    </div>

                    {/* PayFast Subscription Details */}
                    {subscription.payfastConnected && !subscription.payfastError && (
                      <>
                        <div className="detail-row">
                          <span className="label">PayFast Status:</span>
                          <span className="value">{subscription.payfastStatus}</span>
                        </div>
                        
                        {subscription.payfastAmount && (
                          <div className="detail-row">
                            <span className="label">PayFast Amount:</span>
                            <span className="value">R{(subscription.payfastAmount / 100).toFixed(2)}</span>
                          </div>
                        )}
                        
                        {subscription.payfastCycles && (
                          <div className="detail-row">
                            <span className="label">Billing Cycles:</span>
                            <span className="value">
                              {subscription.payfastCyclesComplete || 0} / {subscription.payfastCycles === 0 ? '∞' : subscription.payfastCycles}
                            </span>
                          </div>
                        )}
                        
                        {subscription.payfastRunDate && (
                          <div className="detail-row">
                            <span className="label">Next Bill Date:</span>
                            <span className="value">
                              {new Date(subscription.payfastRunDate).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        
                        {subscription.payfastToken && (
                          <div className="detail-row">
                            <span className="label">PayFast Token:</span>
                            <span className="value" style={{ fontSize: '10px', fontFamily: 'monospace' }}>
                              {subscription.payfastToken.substring(0, 20)}...
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {/* PayFast Error Display */}
                    {subscription.payfastError && (
                      <div className="detail-row">
                        <span className="label">PayFast Issue:</span>
                        <span className="value" style={{ color: '#e53e3e', fontSize: '12px' }}>
                          {subscription.payfastError}
                        </span>
                      </div>
                    )}

                    {/* Firebase Data */}
                    <div className="detail-row">
                      <span className="label">Firebase Status:</span>
                      <span className="value">{subscription.firebaseStatus || 'Unknown'}</span>
                    </div>
                    
                    {subscription.lastSync && (
                      <div className="detail-row">
                        <span className="label">Last Sync:</span>
                        <span className="value">{subscription.lastSync}</span>
                      </div>
                    )}
                    
                    {subscription.lastPayment && (
                      <div className="detail-row">
                        <span className="label">Last Payment:</span>
                        <span className="value">
                          {new Date(subscription.lastPayment).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    
                    {subscription.createdAt && (
                      <div className="detail-row">
                        <span className="label">Joined:</span>
                        <span className="value">
                          {new Date(subscription.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          // Normal user view - show their subscription
          subscriptions.length === 0 ? (
            <div className="no-subscriptions">
              <h3>Unable to Load Account Information</h3>
              <p>Please try refreshing to load your current tier information.</p>
              <div className="no-subscriptions-actions">
                <button 
                  onClick={refreshSubscriptions} 
                  className="upgrade-button"
                >
                  🔄 Refresh Account
                </button>
              </div>
            </div>
          ) : (
            <div className="subscriptions-grid">
              {subscriptions.map((subscription, index) => (
              <div key={subscription.id || index} className="subscription-card">
                <div className="subscription-header">
                  <h3>Current Tier: {subscription.plan || 'Unknown'}</h3>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(subscription.status) }}
                  >
                    {subscription.status || 'Unknown'}
                  </span>
                </div>

                <div className="subscription-details">
                  <div className="detail-row">
                    <span className="label">Tier:</span>
                    <span className="value">{subscription.plan || 'Unknown'}</span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="label">Monthly Cost:</span>
                    <span className="value">
                      {subscription.amount === 0 ? 'FREE' : `R${subscription.amount.toFixed(2)}/month`}
                    </span>
                  </div>

                  {subscription.amount > 0 && (
                    <div className="detail-row">
                      <span className="label">PayFast ID:</span>
                      <span className="value" style={{ fontSize: '12px', color: '#666' }}>
                        {subscription.id?.startsWith('TIER_') ? 'Virtual ID (will get real ID after payment)' : subscription.id}
                      </span>
                    </div>
                  )}
                  
                  {subscription.next_billing_date && (
                    <div className="detail-row">
                      <span className="label">Next Billing:</span>
                      <span className="value">
                        {new Date(subscription.next_billing_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  
                  {subscription.last_payment && (
                    <div className="detail-row">
                      <span className="label">Last Payment:</span>
                      <span className="value">
                        {new Date(subscription.last_payment).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  
                  <div className="detail-row">
                    <span className="label">Active Since:</span>
                    <span className="value">
                      {subscription.created_at ? 
                        new Date(subscription.created_at).toLocaleDateString() : 
                        'N/A'
                      }
                    </span>
                  </div>
                </div>

                {subscription.status?.toLowerCase() === 'active' && subscription.plan !== 'FREE' && (
                  <div className="subscription-actions">
                    <button
                      onClick={() => cancelSubscription(
                        subscription.id, 
                        subscription.plan
                      )}
                      disabled={cancellingSubscription === subscription.id}
                      className="cancel-button"
                    >
                      {cancellingSubscription === subscription.id ? 
                        'Downgrading to FREE...' : 
                        'Downgrade to FREE'
                      }
                    </button>
                  </div>
                )}
                
                {subscription.plan === 'FREE' && (
                  <div className="subscription-actions">
                    <span className="free-note">FREE tier - Upgrade anytime in Dashboard!</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
      </div>
    </div>
  );
};

export default Accounts;